---
title: "The cheapest SERP API in 2026, priced at 10k, 100k, and 1M searches"
description: "Seven Google SERP APIs priced at three monthly volumes, with the same 20 searches sent through five of them. The cheapest one changes with volume; here's where."
date: "2026-09-15"
author: "Kevin Wang"
tags: ["serp", "google-search", "api", "pricing", "comparison"]
keyword: "cheapest serp api"
ogImage: "/og/cheapest-serp-api.png"
---

export const billAt100k = [
  { label: "AnyAPI", sku: "google.search", calls: 100000 },
  { label: "DataForSEO queue", value: 60 },
  { label: "Serper", value: 75 },
  { label: "Scrapingdog", value: 90 },
  { label: "Bright Data", value: 142.5 },
  { label: "SearchApi.io", value: 250 },
  { label: "SerpApi", value: 725 },
];

export const faqItems = [
  {
    q: "Is it cheaper to scrape Google myself than to pay for a SERP API?",
    a: "Rarely, once you count the proxies. A plain server request to Google from a datacenter IP gets a consent page or a CAPTCHA, so a self-built scraper needs residential or mobile proxies, a parser that survives Google's layout changes, and a retry budget for the calls that still fail. Every vendor on this page is selling that upkeep, and the cheapest of them charges well under a tenth of a cent per search.",
  },
  {
    q: "Does a cheaper SERP API return worse results?",
    a: "Not in this test. The five APIs I measured returned between 8.6 and 9.0 organic results per search, and the rankings matched. What changes with price is the extra fields: the dearer responses carry the date, sitelinks, domain, favicon, and highlighted words, and the cheapest carry position, title, link, and snippet only.",
  },
  {
    q: "Do these prices cover Google Maps, News, and Images too?",
    a: "Usually, but check. Of the 13 providers that publish a price for both Google web search and Maps, 11 charge exactly the same per 1,000 for each, according to the price index at serpprices.com. The two that do not charge a multiple of the web rate. Only half the market prices Maps at all, so coverage is the real constraint rather than price.",
  },
  {
    q: "Do unused SERP API credits expire?",
    a: "On most plans, yes. Of the 26 search APIs tracked by serpprices.com, 14 reset unused searches at the end of every billing cycle, 6 let credits roll over indefinitely, and 2 sell prepaid packs with a stated validity window. That is why a plan you only half use can cost double what the pricing page implies.",
  },
  {
    q: "Can I test a SERP API without a credit card?",
    a: "Yes, on most of them. 22 of the 26 providers offer free searches and 16 of those start without a card. Bright Data's 5,000 free searches a month is the largest, Serper gives 2,500 free queries once, and AnyAPI puts free credit in a new wallet. Free searches are a trial, though, not a price, so it should not decide which one you buy.",
  },
];

export const medianSeconds = [
  { label: "Serper", value: 0.9 },
  { label: "SearchApi.io", value: 3.9 },
  { label: "Bright Data", value: 4.5 },
  { label: "DataForSEO live", value: 8.6 },
  { label: "AnyAPI", value: 19.9 },
];

<Callout type="tip" title="Key findings">

Under 5,000 searches a month, [Bright Data](https://brightdata.com/products/serp-api) is free. From there to about 875,000 a month, [AnyAPI](https://getanyapi.com/api/google) has the lowest bill at <Price sku="google.search" /> per 1,000, but it was the slowest API in this test by a wide margin, so if you need the answer while someone waits, pay [Serper](https://serper.dev/) $1.00. Batch jobs should use [DataForSEO](https://dataforseo.com/apis/serp-api)'s $0.60 queue, which answered in twelve seconds, not the 45 minutes its own footnote allows. Past 875,000 a month, [Scrapingdog](https://www.scrapingdog.com/)'s $350 plan is the cheapest if you use all of it.

</Callout>

A SERP API sends a query to Google for you and returns the results as JSON. Every vendor on this page does that same job, and the prices for it run from <Price sku="google.search" /> to $25 per 1,000 searches, which is a 60x spread for a first page of ten links.

I priced seven of them at 10,000, 100,000, and 1,000,000 searches a month, then sent the same 20 searches through the five that would give me a key. Two things came out of that which the pricing pages don't say: the cheapest option was also the slowest, and the "queue" that everyone assumes means waiting came back in seconds.

Here's the short version. The cheapest SERP API depends on your volume and on whether a person is waiting for the result. Below 875,000 searches a month it's AnyAPI on price and Serper on speed. Above that it's Scrapingdog, on a plan.

## The cheapest SERP APIs

- [Bright Data](#best-serp-api-for-under-5000-searches-a-month) for under 5,000 searches a month
- [AnyAPI](#best-serp-api-for-the-lowest-bill-when-nobody-is-waiting) for the lowest bill when nobody is waiting
- [Serper](#best-serp-api-for-cheap-and-fast) for cheap and fast
- [DataForSEO](#best-serp-api-for-batch-jobs) for batch jobs
- [Scrapingdog](#best-serp-api-for-over-875000-searches-a-month) for over 875,000 searches a month
- [SearchApi.io](#best-serp-api-for-the-richest-result-fields) for the richest result fields
- [SerpApi](#best-serp-api-for-multi-engine-coverage) for multi-engine coverage

## What makes a SERP API cheap?

You have a list of queries and a program that needs Google's first page for each one. You want the smallest bill for that, and you don't want to find out about the surcharges after the first invoice. Here's what I compared them on.

- **The bill at your volume, not the listed price.** Minimums, plan tiers, and credit packs mean the price per 1,000 changes with how many you buy. Every option is priced at 10,000, 100,000, and 1,000,000 searches a month.
- **What a search actually costs in the vendor's unit.** Two vendors sell credits, and a Google search costs more than one credit on both.
- **Whether the cheap tier is the real-time tier.** One vendor's headline price is a queue. I measured how long the queue took.
- **What comes back.** Organic results with position, title, link, and snippet at minimum, as JSON. Extra fields are worth something, but only if you'll read them.

<Callout type="note" title="How this was tested, and its limits">

I didn't grade uptime, accuracy, or engines other than Google. Speed comes from 20 identical US English searches per vendor, sent one after another from one laptop, so it's a smell test for speed and response shape.

AnyAPI's 19.9 second median is what happened on September 15, 2026. Its catalog reports a 1.1 second median over the trailing 30 days, so this run may be an unusually bad day for its cheapest source, or the new normal.

Scrapingdog and SerpApi weren't measured: the Scrapingdog free account was out of credits and SerpApi issues a key only after it confirms a phone number.

Every bill assumes one first-page US desktop search per query, no operators, and no retries. Deep pages, `site:` queries, and locations each cost more on the vendors that charge for them.

The bill numbers assume AnyAPI's <Price sku="google.search" /> source serves, and the Serper and Scrapingdog bills assume you use the pack or plan in full. A pack you don't finish costs more per search than the table shows.

Prices checked on September 15, 2026. Enterprise deals aren't included.

Seven vendors aren't the whole market.

[serpprices.com](https://serpprices.com/) tracks the list price of 26 search APIs normalized to dollars per 1,000 searches at four monthly volumes, with credit multipliers, plan floors, and credit expiry applied, and publishes the whole table as [JSON](https://serpprices.com/prices.json) under CC BY 4.0. Use it to check a vendor this page skipped.

</Callout>

## The cheapest SERP APIs at a glance

Monthly bill for Google web searches, using the cheapest published self-serve way to buy each volume.

| API | Best for | 10,000 a month | 100,000 a month | 1,000,000 a month |
| --- | --- | --- | --- | --- |
| Bright Data | Under 5,000 a month | $7.50 | $142.50 | $1,305.00 |
| AnyAPI | Lowest bill, nobody waiting | <Price sku="google.search" calls={10000} /> | <Price sku="google.search" calls={100000} /> | <Price sku="google.search" calls={1000000} /> |
| Serper | Cheap and fast | $10.00 | $75.00 | $750.00 |
| DataForSEO (queue) | Batch jobs | $6.00 | $60.00 | $600.00 |
| DataForSEO (live) | Batch jobs | $20.00 | $200.00 | $2,000.00 |
| Scrapingdog | Over 875,000 a month | $40.00 | $90.00 | $350.00 |
| SearchApi.io | Richest result fields | $40.00 | $250.00 | $1,500.00 |
| SerpApi | Multi-engine coverage | $150.00 | $725.00 | $3,750.00 |

<Chart caption="Monthly bill for 100,000 Google searches on each API's cheapest self-serve option, prices checked 2026-09-15. Serper is $75 on its $375 pack of 500,000 credits; Scrapingdog is its $90 Standard plan; Bright Data is $1.50 per 1,000 after the free 5,000.">
  <BarChart data={billAt100k} showValues valueSuffix="" baseline={0} height={240} format={(n) => `$${n.toFixed(0)}`} ariaLabel="Monthly bill for 100,000 searches" />
</Chart>

Then the same 20 searches through each API that gave me a key, on September 15, 2026.

| API | Calls | Came back with results | Median time | Organic results per call | Fields on a result |
| --- | --- | --- | --- | --- | --- |
| Serper | 20 | 20 | 0.9 s | 8.9 | position, title, link, snippet, date, sitelinks |
| SearchApi.io | 20 | 20 | 3.9 s | 8.6 | Serper's set plus domain, favicon, source, highlighted words |
| Bright Data | 20 | 16 | 4.5 s | 8.6 | rank, title, link, description, source, extensions |
| DataForSEO (live) | 20 | 20 | 8.6 s | 8.6 | rank, title, url, description, domain, breadcrumb |
| AnyAPI | 40 | 40 | 19.9 s | 9.0 | position, title, link, snippet |

<Chart caption="Median response time for the same 20 Google searches per API, measured 2026-09-15 from one machine, in seconds. AnyAPI's figure is over 40 calls. Scrapingdog and SerpApi aren't plotted: the Scrapingdog free account had run out of credits and SerpApi issues a key only after it confirms a phone number.">
  <BarChart data={medianSeconds} showValues valueSuffix="" baseline={0} height={220} format={(n) => `${n.toFixed(1)} s`} ariaLabel="Median response time in seconds: Serper 0.9, SearchApi.io 3.9, Bright Data 4.5, DataForSEO live 8.6, AnyAPI 19.9." />
</Chart>

Fewer than ten results is normal now. Google's first page carries an AI overview, a "people also ask" block, and a video row, and the organic list is what's left. All five APIs returned fewer than ten organic results on most calls.

## Best SERP API for under 5,000 searches a month

### Bright Data

<Figure src="/og/screenshots/rank-tracking-api-bright-data-rank-tracking-price.png" alt="Bright Data's SERP API pricing page showing $1.50 per 1,000 results pay as you go with no commitment, and $1.30 per 1,000 on the $499 monthly plan" width={1280} height={900} />

**Bright Data pros:**

- The first 5,000 requests every month are free, with no card and no expiry
- $1.50 per 1,000 after that with no plan and no minimum
- You're billed only when a request succeeds

**Bright Data cons:**

- Four of my 20 searches came back as something other than the JSON I asked for
- Above 100,000 a month it's the second most expensive option here

If you run a few thousand searches a month, Bright Data is free and you can stop reading. Five thousand a month is 160 searches a day, and there's nothing to size or renew. Past that the rate is $1.50 per 1,000, which is fair but almost four times AnyAPI's and half again Serper's.

Check what comes back before you write the parser. I asked for JSON on every call, and four of 20 responses weren't parseable as JSON. Sixteen were clean, with rank, title, link, description, and Google's own extensions on each organic result. That could be my zone's configuration, so test yours.

**Bright Data pricing:** 5,000 requests a month free, then $1.50 per 1,000 pay as you go. The $499 Scale plan includes 380,000 and charges $1.30 per 1,000 above that.

## Best SERP API for the lowest bill when nobody is waiting

### AnyAPI

**AnyAPI pros:**

- The lowest bill from 5,000 to about 875,000 searches a month, at <Price sku="google.search" /> per 1,000
- No plan, no minimum, no expiring credits; you put money in a wallet and each search takes a fraction of a cent out
- Every result is the same four fields: position, title, link, snippet

**AnyAPI cons:**

- The slowest API in this test: a median of 19.9 seconds and a worst call of 32.8
- 27 of my 40 calls spent 15 seconds on a source that never answered before a second one did
- The result carries no date, sitelinks, or domain

**The cheapest per-search price on this page came with the slowest response in the test, and the two facts have the same cause.** AnyAPI routes each search to the cheapest of four sources that can serve it. In this run the cheapest source hit its 15 second timeout on 27 of 40 calls and a second source then answered in about four, so those calls took both waits end to end. Twelve calls did get an answer from the first source and came back in three to fifteen seconds.

For a cron job that fills a table overnight, none of that matters and the bill is the point: <Price sku="google.search" calls={100000} /> for 100,000 searches. For a "search now" button, a 20 second median is unusable, and Serper at $1.00 is the right call. The request accepts a `preferLatencyUnderMs` hint that routes to a faster source at its price; when I set it to 3,000, the bill moved to $0.70 and $0.90 per 1,000 and nine of 20 calls still took over 17 seconds, so it didn't rescue this run. {/* receipt 2026-09-15 */}

**AnyAPI pricing:** <Price sku="google.search" /> per 1,000, billed per request from a prepaid wallet, rising to <Price sku="google.search" failover /> if you ask for a faster source with the latency hint. New accounts get free credit and no card is required.

## Best SERP API for cheap and fast

### Serper

<Figure src="/og/screenshots/rank-tracking-api-serper-starter-topup.png" alt="Serper's pricing page showing a $50 pack of 50,000 credits at $1.00 per 1,000 and a $375 pack of 500,000 at $0.75, valid for six months" width={1280} height={900} />

**Serper pros:**

- The fastest API in the test: 0.9 seconds at the median, 3.2 at the worst
- 2,500 free queries with no card, enough to build a real prototype
- Adds a date and sitelinks to the four basic fields

**Serper cons:**

- You buy credits in packs that expire in six months, so a light user pre-pays for months of searches
- $50 is the smallest pack

Serper is the one I'd pick for anything where a person or an agent is waiting. Every one of 20 calls came back in under four seconds, and most in under one. The response is Google's organic list with position, title, link, snippet, a date where Google shows one, and sitelinks where it shows those.

The pricing model is the catch. $50 buys 50,000 credits, one per search, and they're valid for six months. If you run 10,000 searches a month you'll use the pack in five, so your effective rate is the full $1.00 per 1,000 and your monthly bill is $10. If you run 1,000 a month, the pack expires with 44,000 credits unused and you've paid $50 for 6,000 searches. The $375 pack drops the rate to $0.75, which is the figure in the table at 100,000 and above.

**Serper pricing:** 2,500 free queries. $50 for 50,000 credits ($1.00 per 1,000), $375 for 500,000 ($0.75 per 1,000), credits valid for six months.

## Best SERP API for batch jobs

### DataForSEO

<Figure src="/og/screenshots/rank-tracking-api-dataforseo-serp-modes.png" alt="DataForSEO's Google SERP pricing page showing Standard Queue at $0.60, Priority Queue at $1.20, and Live Mode at $2.00 per 1,000" width={1280} height={900} />

**DataForSEO pros:**

- The Standard Queue is $0.60 per 1,000, and all five tasks I queued were finished within twelve seconds of posting
- The richest response of the cheap options: rank, absolute rank, domain, breadcrumb, and the SERP feature type on every item
- A sandbox you can code against before paying

**DataForSEO cons:**

- The queue is asynchronous: you post tasks, then fetch results in a second call, so it's a different program from the others
- The live mode that behaves like the other APIs costs $2.00 per 1,000 and had an 8.6 second median here
- `site:`, `inurl:`, and `filetype:` each multiply the price by five, and a `$50` minimum deposit applies

DataForSEO sells the same Google search at three prices and the cheap one is a queue. The pricing page says results arrive in "5 minutes on average" and a footnote allows up to 45. **I posted five tasks to the Standard Queue and every one was complete between five and twelve seconds later.** That makes the $0.60 rate the cheapest fast option on this page, provided your code can post a batch and collect it, which for a nightly job it can.

The live endpoint is the one to compare with Serper and AnyAPI, and at $2.00 per 1,000 and 8.6 seconds it loses to both. Use it only when you need DataForSEO's field set in real time.

Read the modifiers before you estimate a bill. A search with a `site:` operator is $3.00 per 1,000 on the queue, and asking for more than ten results bills again per extra ten.

**DataForSEO pricing:** Standard Queue $0.60, Priority Queue $1.20, Live $2.00 per 1,000, pay as you go with a $50 minimum deposit. Operators multiply the price by five each.

## Best SERP API for over 875,000 searches a month

### Scrapingdog

<Figure src="/og/screenshots/rank-tracking-api-scrapingdog-google-serp-plan.png" alt="Scrapingdog's pricing page showing the $40 Lite plan with 200,000 credits and a Google SERP request costing 5 credits" width={1280} height={900} />

**Scrapingdog pros:**

- Above about 875,000 searches a month the Premium plan is the cheapest way to buy Google results here
- The $350 Premium plan covers 1,200,000 searches, which beats every pay-as-you-go rate on this page at that volume
- Failed requests aren't charged

**Scrapingdog cons:**

- A Google search costs 5 credits, so every plan's "credits" number is five times the search count
- Plans are monthly subscriptions and unused credits don't roll over
- The free account's 200 credits are 40 searches, and mine had run out before this test

The credit unit is the thing to know. Scrapingdog's page says 200,000 credits for $40, which reads like $0.20 per 1,000 until you find the line that a Google SERP request is 5 credits. The Lite plan is 40,000 searches, $1.00 per 1,000, the same as Serper's pack. The Standard plan is $90 for 1,000,000 credits, which is 200,000 searches at $0.45 per 1,000, above AnyAPI's <Price sku="google.search" />. Premium is where the plans win: $350 for 6,000,000 credits is 1,200,000 searches at $0.29 per 1,000, the cheapest rate on this page. Both numbers assume you spend the whole plan every month. Use half of Premium and you've paid $0.58 per 1,000.

I couldn't measure speed this time. The free account I made three weeks ago had 200 credits, I'd spent them, and the API returned "You have reached your account limit". In an earlier run it was the fastest option, tied with Serper.

**Scrapingdog pricing:** Lite $40 a month for 200,000 credits, Standard $90 for 1,000,000, Premium $350 for 6,000,000. A Google search is 5 credits. 200 free credits to start.

## Best SERP API for the richest result fields

### SearchApi.io

**SearchApi.io pros:**

- Every organic result carries the domain, favicon, source name, displayed link, and the words Google bolded
- 100 free requests, no card
- Clean plan ladder with a published rate at every tier

**SearchApi.io cons:**

- $4.00 per 1,000 on the cheapest plan, ten times AnyAPI and four times Serper
- 3.9 seconds at the median, with two calls over ten

SearchApi.io returned the most useful result object of the five I measured. If your program reads the displayed URL, wants the favicon, or needs the snippet's highlighted words for a relevance score, it's all there without a second request. That's worth $4.00 per 1,000 to some readers.

For a first page of links it isn't. The $40 Developer plan buys 10,000 searches; the same 10,000 is <Price sku="google.search" calls={10000} /> on AnyAPI or $10 on Serper. The rate falls to $2.50 at 100,000 and $1.50 at 1,000,000, which is still above every cheap option here at every volume.

**SearchApi.io pricing:** Developer $40 a month for 10,000 searches, BigData $250 for 100,000, Octo 1M $1,500 for 1,000,000. 100 free requests.

## Best SERP API for multi-engine coverage

### SerpApi

<Figure src="/og/screenshots/rank-tracking-api-serpapi-monthly-plans.png" alt="SerpApi's pricing page showing Starter at $25 for 1,000 searches and the monthly plan ladder above it" width={1280} height={900} />

**SerpApi pros:**

- The most engines, the most thorough docs, and a support inbox that answers
- Cached, errored, and failed searches don't count against the plan
- Every block on the page is parsed: ads, knowledge panel, related questions, the lot

**SerpApi cons:**

- $25 a month for 1,000 searches, which is $25 per 1,000, sixty times AnyAPI's billed rate
- 100,000 a month is the $725 plan; 1,000,000 is $3,750
- 250 free searches a month, and a key is issued only after it confirms a phone number

SerpApi is the incumbent, and on a "cheapest" list it's here as the ceiling. The Starter plan is $25 per 1,000. At 1,000,000 searches a month it's $3,750, against $350 at Scrapingdog and <Price sku="google.search" calls={1000000} /> at AnyAPI.

What you get for that is the whole Google page as structured data, across Google, Bing, YouTube, Amazon, and a long list of other engines, with documentation for every field. If you're building on several engines or you want a vendor with people behind it, that's the case for it. For Google organic results on a budget there isn't one.

**SerpApi pricing:** 250 free searches a month. Starter $25 for 1,000, Developer $75 for 5,000, Searcher $725 for 100,000, Cloud 1M $3,750 for 1,000,000. Only successful searches count.

## Which SERP API should you use?

- Under 5,000 searches a month: Bright Data, free, and check that your responses are JSON.
- A prototype today with no card: Serper's 2,500 free queries.
- 5,000 to 875,000 a month and nobody is waiting on the result: AnyAPI.
- 5,000 to 875,000 a month and someone is: Serper.
- A nightly batch you can post and collect: DataForSEO's Standard Queue.
- Over 875,000 a month and you'll use the plan: Scrapingdog Premium.
- You need the domain, favicon, and highlighted words on every result: SearchApi.io.
- Several engines and a support desk: SerpApi.

<FAQ items={faqItems} />

If you're building a rank tracker rather than a general search feed, the [rank tracking API comparison](/blog/rank-tracking-api) prices the same vendors per keyword check. If you're leaving SerpApi specifically, the [SerpApi alternatives page](/alternatives/best-serpapi-alternative) compares the responses field by field. The [Google Search API page](/api/google) has the live request format and the current price of each source.

<CTA to="/api/google" note="Google results as clean JSON, billed per search from a prepaid wallet, no plan and nothing that expires. New accounts get free credit, no card required.">
  Try the Google Search API
</CTA>
